import {
  useState, useEffect, useRef, ReactElement, KeyboardEvent, Fragment,
  useMemo,
} from "react"
import {Fzf, extendedMatch} from "fzf"
import "./TextWithSuggestions.scss"

function createIntervalsFromSet(set: Set<number>): ReadonlyArray<[number, number]> {
  const inOrder = [...set].sort((a, b) => a - b)
  return inOrder.reduce((prev, newItem) => {
    if (prev.length === 0) {
      return [[newItem, newItem + 1]]
    }
    const lastItem = prev[prev.length - 1]
    if (lastItem[1] === newItem) {
      return [...prev.slice(0, -1), [lastItem[0], newItem + 1]]
    }
    return [...prev, [newItem, newItem + 1]]
  }, new Array<[number, number]>())
}

type Props = {
  optionsKeyAndValue: ReadonlyArray<[string, string]>
  value: string
  onChange: (newValue: string) => void
  className?: string
} & {
  [K in string as `data-${K}`]: string
}

export default function SearchableSelect(
  {optionsKeyAndValue, className, value, onChange, ...dataAttributes}: Props
): ReactElement {
  const thisElement = useRef<HTMLDivElement>(null)
  const [textValue, setTextValue] = useState("")
  const [isOpen, setIsOpen] = useState(false)
  const [currentlySelectedIndex, optionItems] = useMemo(() => {

    const optionItems = new Fzf(optionsKeyAndValue,
      {selector: x => x[1], match: extendedMatch,}).find(
        textValue)

    let currentlySelectedIndex: number = optionItems.findIndex(
      ({item: [key]}) => key === value)
    if (currentlySelectedIndex !== -1) {
      return [currentlySelectedIndex, optionItems]
    }
    return [0, [{
      item: optionsKeyAndValue.find(([key]) => key === value)!,
      positions: new Set<number>(),
      score: -1,
      start: -1,
      end: -1,
    }, ...optionItems]]
  }, [textValue, optionsKeyAndValue, value])
  const [selectedIndex, setSelectedIndex] = useState(currentlySelectedIndex)

  useEffect(() => {
    setSelectedIndex(currentlySelectedIndex)
  }, [optionItems, currentlySelectedIndex])

  useEffect(() => {
    if (!thisElement.current) {
      return
    }
    if (!isOpen) {
      return
    }
    const selectedItem = thisElement.current.querySelector(".item.selected")
    if (selectedItem) {
      selectedItem.scrollIntoView({block: "nearest"})
    }
  }, [selectedIndex, isOpen])

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    switch (e.code) {
      case "ArrowDown":
      case "ArrowUp":
        if (!isOpen) {
          setIsOpen(true)
        } else {
          const diff = e.code === "ArrowDown" ? 1 : -1
          setSelectedIndex(
            si => (optionItems.length === 0) ? currentlySelectedIndex :
            (si + diff + optionItems.length) % optionItems.length)
        }
        break;
      case "Tab":
      case "Enter":
        if (!isOpen) {
          setIsOpen(true)
        } else {
          onChange(optionItems[selectedIndex].item[0])
          setIsOpen(false)
        }
        break;
      case "Escape":
        setSelectedIndex(currentlySelectedIndex)
        setIsOpen(false)
        break;
      default:
        return
    }
    e.preventDefault()
  }

  useEffect(() => {
    if (!isOpen) {
      setTextValue("")
    }
  }, [isOpen])


  return <div
    className={(className ? className + " " : "") + "TextWithSuggestions Select"}
    data-value={value}
    ref={thisElement}>
    <span onClick={() => setIsOpen(true)}>
      <input
        {...dataAttributes}
        type="text"
        value={isOpen ? textValue : optionItems[currentlySelectedIndex].item[1]}
        onKeyDown={onKeyDown}
        onChange={e => setTextValue(e.currentTarget.value)}
        onFocus={() => setIsOpen(true)}
        // need a small delay for the blur, since it may be caused by a click on
        // the list
        onBlur={() => setTimeout(() => setIsOpen(false), 200)}
      />
    </span>
    {isOpen ?
      <div className="options">
        <ul>
          {optionItems.length === 0 && <span>No results</span>}
          {optionItems.map(({item: [insertValue, showValue], positions}, index) => {
            const intervals = createIntervalsFromSet(positions)
            return <li className={[
              index === selectedIndex ? ["selected"] : [],
              value === insertValue ? ["currentlySelected"] : [],
              ["item"]
            ].flat().join(" ")}
              onMouseEnter={() => setSelectedIndex(index)}
              data-value={insertValue}
              key={index} onClick={() => {onChange(insertValue); setIsOpen(false)}}>
              {intervals.map((interval, index) => {
                const prevIntervalEnd = index === 0
                  ? (interval[0] === 0 ? null : 0)
                  : intervals[index - 1][1]
                return <Fragment key={index}>
                  {prevIntervalEnd !== null && <span className="nohl">
                    {showValue.slice(prevIntervalEnd, interval[0])}
                  </span>}
                  <span className="hl">{showValue.slice(...interval)}</span>
                </Fragment>
              })}
              <span className="nohl">
                {showValue.slice(
                  intervals.length === 0 ? 0 : intervals[intervals.length - 1][1])}
              </span>
            </li>
          })
          }
        </ul>
      </div>
      : null}
    </div>

}

